import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { Pagination } from 'src/models/pagination.model';
import { RoleWithCapabilities } from 'src/models/user.model';
import { closeModal } from './modal.reducer';

type InitialState = {
  fetchLoading: boolean;
  fetchFailure: boolean;
  fetchSuccess: boolean;
  createLoading: boolean;
  createSuccess: boolean;
  createFailure: boolean;
  loading: boolean;
  failure: boolean;
  success: boolean;
  roles: RoleWithCapabilities[];
  totalItems: number;
  refresh: number;
  roleName: string;
};

const INITIAL_STATE: InitialState = {
  fetchLoading: false,
  fetchFailure: false,
  fetchSuccess: false,
  createLoading: false,
  createSuccess: false,
  createFailure: false,
  loading: false,
  failure: false,
  success: false,
  roles: [] as RoleWithCapabilities[],
  totalItems: 0,
  refresh: 0,
  roleName: null as unknown as string,
};

export const getRoles = createAsyncThunk('roles/get', async (data: { pagination: Pagination }, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<{
      data: RoleWithCapabilities[];
      totalItems: number;
    }> = await axios.get(`/api/roles`, {
      params: data.pagination,
    });

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const createNewRole = createAsyncThunk('roles/post', async (data: { name: string }, { rejectWithValue, dispatch }) => {
  try {
    const response: AxiosResponse<string> = await axios.post(`/api/roles`, { name: data.name });
    dispatch(refreshRoles());
    dispatch(closeModal());
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const deleteRole = createAsyncThunk('roles/delete', async (data: { roleId: number }, { rejectWithValue, dispatch }) => {
  try {
    await axios.delete(`/api/roles/${data.roleId}`);
    dispatch(refreshRoles());
    dispatch(closeModal());
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const updateRoleName = createAsyncThunk(
  'roles/update',
  async (data: { roleId: number; newName: string }, { rejectWithValue, dispatch }) => {
    try {
      await axios.put(`/api/roles/name`, { roleId: data.roleId, newName: data.newName });
      dispatch(refreshRoles());
      dispatch(closeModal());
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const rolesSlice = createSlice({
  name: 'roles',
  initialState: INITIAL_STATE,
  reducers: {
    refreshRoles: state => {
      return {
        ...state,
        refresh: state.refresh + 1,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(getRoles.pending, state => {
      return {
        ...state,
        fetchLoading: true,
        fetchFailure: false,
        fetchSuccess: false,
      };
    });

    builder.addCase(getRoles.fulfilled, (state, action: PayloadAction<{ data: RoleWithCapabilities[]; totalItems: number }>) => {
      return {
        ...state,
        fetchLoading: false,
        fetchFailure: false,
        fetchSuccess: true,
        roles: action.payload.data,
        totalItems: action.payload.totalItems,
      };
    });
    builder.addCase(getRoles.rejected, state => {
      return {
        ...state,
        fetchLoading: false,
        fetchFailure: true,
        fetchSuccess: false,
      };
    });
    builder.addCase(createNewRole.pending, state => {
      return {
        ...state,
        createLoading: true,
        createFailure: false,
        createSuccess: false,
      };
    });

    builder.addCase(createNewRole.fulfilled, (state, action: PayloadAction<string>) => {
      return {
        ...state,
        createLoading: false,
        createFailure: false,
        createSuccess: true,
        roleName: action.payload,
      };
    });
    builder.addCase(createNewRole.rejected, state => {
      return {
        ...state,
        createLoading: false,
        createFailure: true,
        createSuccess: false,
      };
    });
    builder.addCase(deleteRole.pending, state => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(deleteRole.fulfilled, state => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(deleteRole.rejected, state => {
      return {
        ...state,
        loading: false,
        failure: true,
      };
    });
    builder.addCase(updateRoleName.pending, state => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(updateRoleName.fulfilled, state => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(updateRoleName.rejected, state => {
      return {
        ...state,
        loading: false,
        failure: true,
      };
    });
  },
});

export const { refreshRoles } = rolesSlice.actions;

export default rolesSlice.reducer;
